import { useSyncExternalStore } from 'react';

/**
 * Layout classes, following the width bands most design systems converge on:
 *   compact  < 640px   phones, portrait
 *   medium   < 1024px  tablets, narrow windows
 *   expanded ≥ 1024px  desktops
 */
export type Breakpoint = 'compact' | 'medium' | 'expanded';

const COMPACT = '(max-width: 639px)';
const MEDIUM = '(max-width: 1023px)';
const COARSE = '(pointer: coarse)';

const subscribeTo = (query: string) => (onChange: () => void) => {
  const mq = window.matchMedia(query);
  mq.addEventListener('change', onChange);
  return () => {
    mq.removeEventListener('change', onChange);
  };
};

const subscribeBreakpoint = (onChange: () => void) => {
  const a = subscribeTo(COMPACT)(onChange);
  const b = subscribeTo(MEDIUM)(onChange);
  return () => {
    a();
    b();
  };
};

const readBreakpoint = (): Breakpoint => {
  if (window.matchMedia(COMPACT).matches) return 'compact';
  if (window.matchMedia(MEDIUM).matches) return 'medium';
  return 'expanded';
};

export const useBreakpoint = (): Breakpoint =>
  useSyncExternalStore(subscribeBreakpoint, readBreakpoint, () => 'expanded');

/** True for touch-first devices; controls grow to comfortable tap targets. */
export const useCoarsePointer = (): boolean =>
  useSyncExternalStore(
    subscribeTo(COARSE),
    () => window.matchMedia(COARSE).matches,
    () => false,
  );
